import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import axios from "axios";

const Step13Contact = forwardRef(({ onChange }, ref) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    postalCode: "",
    country: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ show: false, message: "" });

  useEffect(() => {
    const saved = localStorage.getItem("contactForm");
    if (saved) setFormData(JSON.parse(saved));
  }, []);

  useEffect(() => {
    localStorage.setItem("contactForm", JSON.stringify(formData));
    localStorage.setItem("contactName", formData.name);
    localStorage.setItem("contactEmail", formData.email);

    if (onChange) onChange(validateForm());
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    let fieldError = false;
    if (value.trim() === "") fieldError = true;
    else if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      fieldError = !emailRegex.test(value);
    } else if (name === "phone") {
      const phoneRegex = /^[0-9]{7,15}$/;
      fieldError = !phoneRegex.test(value);
    }

    setErrors((prev) => ({ ...prev, [name]: fieldError }));
  };

  const validateForm = () => {
    const allFilled = Object.values(formData).every((v) => v.trim() !== "");
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
    const phoneValid = /^[0-9]{7,15}$/.test(formData.phone);
    return allFilled && emailValid && phoneValid;
  };

  useImperativeHandle(ref, () => ({
    checkOrCreateUser: async () => {
      if (!validateForm()) {
        setModal({ show: true, message: "Please fill all fields correctly." });
        return false;
      }

      setLoading(true);
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const response = await axios.post(
          `${baseUrl}/check-or-create-user`,
          {
            name: formData.name,
            email: formData.email,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          }
        );

        if (response.data.success) {
          localStorage.setItem("userId", response.data.user_id);
          return true;
        } else {
          setModal({ show: true, message: "Something went wrong. Try again." });
          return false;
        }
      } catch (err) {
        console.error(err);
        setModal({ show: true, message: "Server error. Try again later." });
        return false;
      } finally {
        setLoading(false);
      }
    },
  }));

  return (
    <div className="bg-white flex items-center justify-center px-4 font-poppins relative">
      <div className="w-full relative max-w-lg">
        <h2 className="text-[16px] font-Medium text-black mt-12">
          Contact Information
        </h2>
        <p className="text-[12px] text-[#000000] mb-5">
          Required for prescription. Please complete all mandatory fields.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Name */}
          <div>
            <label className="block text-[16px] font-medium text-black mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              placeholder="Enter your first name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full border rounded-md px-3 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#35C3D9] focus:border-[#35C3D9] ${
                errors.name ? "border-red-500" : "border-gray-200"
              }`}
            />
          </div>
          {/* Phone */}
          <div>
            <label className="block text-[16px] font-medium text-black mb-1">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="phone"
              placeholder="Enter your phone number"
              value={formData.phone}
              onChange={handleChange}
              className={`w-full border rounded-md px-3 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#35C3D9] focus:border-[#35C3D9] ${
                errors.phone ? "border-red-500" : "border-gray-200"
              }`}
            />
          </div>
        </div>

        {/* Email */}
        <div className="mb-4">
          <label className="block text-[16px] font-medium text-black mb-1">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            placeholder="Enter your email address"
            value={formData.email}
            onChange={handleChange}
            className={`w-full border rounded-md px-3 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#35C3D9] focus:border-[#35C3D9] ${
              errors.email ? "border-red-500" : "border-gray-200"
            }`}
          />
        </div>

        {/* Optional fields */}
        {["address", "city", "postalCode", "country"].map((field) => (
          <div className="mb-4" key={field}>
            <label className="block text-[16px] font-medium text-black mb-1">
              {field.charAt(0).toUpperCase() + field.slice(1)}
            </label>
            <input
              type="text"
              name={field}
              placeholder={`Enter your ${field}`}
              value={formData[field]}
              onChange={handleChange}
              className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-black placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#35C3D9] focus:border-[#35C3D9]"
            />
          </div>
        ))}

        {/* Next button */}
      

        {/* Modal */}
        {modal.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg max-w-sm text-center">
              <p className="mb-4">{modal.message}</p>
              <button
                onClick={() => setModal({ show: false, message: "" })}
                className="px-4 py-2 bg-[#35C3D9] text-white font-medium rounded-md"
              >
                OK
              </button>
            </div>
          </div>
        )}

        {/* Full screen loader */}
        {loading && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="w-16 h-16 border-4 border-t-[#35C3D9] border-gray-200 rounded-full animate-spin"></div>
          </div>
        )}
      </div>
    </div>
  );
});

export default Step13Contact;
